import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Typography,
} from '@mui/joy';
import { ArrowDown, ArrowUp, Plus, Trash } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminBoardsAPI } from '../../lib/apiAdmin';
import { getApiErrorMessage } from '../../lib/apiError';
import { LIFECYCLE_META } from './LifecycleStageBar';
import type { LifecycleStage, PlatformBoardKind, PlatformBoardStatus } from '../../types/admin';

const LIFECYCLE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'No stage change' },
  ...(Object.keys(LIFECYCLE_META) as LifecycleStage[]).map((stage) => ({
    value: stage,
    label: LIFECYCLE_META[stage].label,
  })),
];

/**
 * Edit a board's columns.
 *
 * Both the CRM pipeline and the development board are configured here, but the
 * per-column meaning differs: dropping a lead in a CRM column can move its
 * lifecycle stage, while a development column can alert the Customer Success
 * reps waiting on the ticket. Only the relevant control is offered, because a
 * lifecycle stage on a development column would do nothing.
 */
export const BoardColumnsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  boardId: number | null;
  kind: PlatformBoardKind;
}> = ({ open, onClose, boardId, kind }) => {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const statusesQuery = useQuery({
    queryKey: ['admin', 'board-statuses', boardId],
    queryFn: () => AdminBoardsAPI.listStatuses(boardId as number),
    enabled: open && boardId !== null,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'board-statuses', boardId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'board'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'boards'] });
  };

  const fail = (err: unknown) =>
    setError(getApiErrorMessage(err, 'That change could not be saved.'));

  const updateMutation = useMutation({
    mutationFn: ({ id, changes }: { id: number; changes: Partial<PlatformBoardStatus> }) =>
      AdminBoardsAPI.updateStatus(id, changes),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      AdminBoardsAPI.createStatus({
        board: boardId as number,
        name: newName.trim(),
        order: Math.max(-1, ...(statusesQuery.data?.results ?? []).map(row => row.order)) + 1,
      }),
    onSuccess: () => {
      setNewName('');
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => AdminBoardsAPI.deleteStatus(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    // A column holding cards is PROTECTed server-side and comes back as a 409
    // saying how many are in the way; that message is more useful than ours.
    onError: fail,
  });

  const reorderMutation = useMutation({
    mutationFn: (rows: PlatformBoardStatus[]) =>
      AdminBoardsAPI.reorderStatuses(rows.map((row, index) => ({ id: row.id, order: index }))),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const columns = [...(statusesQuery.data?.results ?? [])].sort((a, b) => a.order - b.order);

  const move = (index: number, delta: number) => {
    const next = [...columns];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorderMutation.mutate(next);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 620 }, maxWidth: '100%', maxHeight: '90vh' }}>
        <ModalClose />
        <DialogTitle>Columns</DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
            {error && (
              <Alert color="danger" variant="soft" size="sm">
                {error}
              </Alert>
            )}

            {columns.map((column, index) => (
              <Sheet
                key={column.id}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 'md', display: 'grid', gap: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'grid' }}>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label="Move up"
                      disabled={index === 0 || reorderMutation.isPending}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUp size={14} />
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label="Move down"
                      disabled={index === columns.length - 1 || reorderMutation.isPending}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown size={14} />
                    </IconButton>
                  </Box>

                  <Input
                    size="sm"
                    defaultValue={column.name}
                    onBlur={(event) =>
                      event.target.value.trim() &&
                      event.target.value !== column.name &&
                      updateMutation.mutate({
                        id: column.id,
                        changes: { name: event.target.value.trim() },
                      })
                    }
                    sx={{ flex: 1 }}
                  />

                  <Input
                    size="sm"
                    type="color"
                    defaultValue={column.color || '#94a3b8'}
                    onBlur={(event) =>
                      event.target.value !== column.color &&
                      updateMutation.mutate({ id: column.id, changes: { color: event.target.value } })
                    }
                    sx={{ width: 56, p: 0.5 }}
                  />

                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    aria-label="Delete column"
                    loading={deleteMutation.isPending && deleteMutation.variables === column.id}
                    onClick={() => deleteMutation.mutate(column.id)}
                  >
                    <Trash size={16} />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', pl: 5 }}>
                  <Checkbox
                    size="sm"
                    label="Finished"
                    checked={column.is_done}
                    onChange={(event) =>
                      updateMutation.mutate({
                        id: column.id,
                        changes: { is_done: event.target.checked },
                      })
                    }
                  />

                  {kind === 'dev' && (
                    <Checkbox
                      size="sm"
                      label="Alert Customer Success"
                      checked={column.notify_cs}
                      onChange={(event) =>
                        updateMutation.mutate({
                          id: column.id,
                          changes: { notify_cs: event.target.checked },
                        })
                      }
                    />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      WIP limit
                    </Typography>
                    <Input
                      size="sm"
                      type="number"
                      placeholder="—"
                      slotProps={{ input: { min: 0 } }}
                      defaultValue={column.wip_limit ?? ''}
                      onBlur={(event) => {
                        const value = event.target.value === '' ? null : Number(event.target.value);
                        if (value !== column.wip_limit) {
                          updateMutation.mutate({ id: column.id, changes: { wip_limit: value } });
                        }
                      }}
                      sx={{ width: 80, flexShrink: 0 }}
                    />
                  </Box>

                  {kind === 'crm' && (
                    <Select
                      size="sm"
                      value={column.lifecycle_stage || ''}
                      onChange={(_, value) =>
                        updateMutation.mutate({
                          id: column.id,
                          changes: { lifecycle_stage: (value as string) ?? '' },
                        })
                      }
                      sx={{ minWidth: { xs: 0, sm: 170 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                    >
                      {LIFECYCLE_OPTIONS.map((option) => (
                        <Option key={option.value || 'none'} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Box>
              </Sheet>
            ))}

            {columns.length === 0 && !statusesQuery.isLoading && (
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                This board has no columns yet.
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Input
                size="sm"
                placeholder="New column name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                sx={{ flex: 1 }}
              />
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<Plus size={16} />}
                disabled={!newName.trim() || boardId === null}
                loading={createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Add
              </Button>
            </Box>

            {kind === 'crm' && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                A column with a stage moves the lead's lifecycle when a card lands in it. Leave it
                on "No stage change" for columns that are only a place to put things.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default BoardColumnsModal;
